import { MglMarker, MglNavigationControl, MglGeolocateControl } from '@advisr/v-mapbox';
import MapService from '@/services/mapService';
import { mapState } from 'vuex';
import { randomInt } from '@/utils/vuex';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
const kLayerZIndex1 = 'z-index-1';
const kLayerZIndex2 = 'z-index-2';
export default {
  name: 'NiotaClassicGwMgmtMapInstance',
  components: {
    MglMarker,
    MglNavigationControl,
    MglGeolocateControl
  },
  props: {
    cameraObj: {
      type: Object,
      required: true
    },
    animateFlyIn: {
      type: Boolean,
      required: false,
      default: () => true
    },
    items: {
      type: Array,
      required: true
    },
    clusters: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      realCamera: {
        center: [9.7027, 52.3736],
        zoom: 7,
        bearing: randomInt(90) - 90
      },
      clusterRefreshInterval: null,
      hasLoadedMarkers: false,
      lastBoundsRefresh: {
        _ne: {
          lng: 0,
          lat: 0
        },
        _sw: {
          lng: 0,
          lat: 0
        }
      },
      selectedItem: {}
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    markerColor() {
      return MapService.markerColor(this.color, this.$store);
    }
  },
  watch: {
    items() {
      if (this.hasLoadedMarkers) {
        return;
      }
      if (this.map && this.items.length > 0 || this.clusters.length > 0) {
        this.hasLoadedMarkers = true;
        if (this.map) {
          this.flyToCamera();
        }
      }
    },
    clusters() {
      if (this.hasLoadedMarkers) {
        return;
      }
      if (this.map && (this.items.length > 0 || this.clusters.length > 0)) {
        this.hasLoadedMarkers = true;
        if (this.map) {
          this.flyToCamera();
        }
      }
    },
    mapStyle() {
      // if the map-style changes, need to reload the map, in order for the polygons to render again
      this.addZIndexHelperLayers();
    }
  },
  created() {
    this.map = null;
  },
  methods: {
    closePopup() {
      this.selectedItem = {};
    },
    onMapLoaded(event) {
      this.map = event.map;
      this.addZIndexHelperLayers();
      if (this.map) {
        this.flyToCamera();
      }
      // Add 3D Buildings in case switched on in filter
      if (this.$store.state.app.map3DBuild) {
        MapService.add3DBuildingLayer(this.map);
      }
      this.$emit('mapLoad', event.map);
    },
    flyToCamera: debounce(function () {
      setTimeout(() => {
        this.map.flyTo({
          center: this.cameraObj.center,
          pitch: 15,
          zoom: this.cameraObj.zoom > 0 ? this.cameraObj.zoom : 7,
          bearing: 0,
          duration: this.animateFlyIn ? 2000 : 0,
          animate: this.animateFlyIn
        });
      }, 100);
    }, 100),
    addZIndexHelperLayers() {
      if (this.map) {
        if (this.map.getLayer(kLayerZIndex1)) {
          this.map.removeLayer(kLayerZIndex1);
        }
        if (this.map.getLayer(kLayerZIndex2)) {
          this.map.removeLayer(kLayerZIndex2);
        }
        if (this.map.getSource('empty')) {
          this.map.removeSource('empty');
        }
        // since there is no direct z-index property, create dummy layers under which stuff is positioned
        this.map.addSource('empty', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
        // TODO: check if zIndex1 and zIndex2 are still needed
        // eslint-disable-next-line no-unused-vars
        const zIndex2 = this.map.addLayer({
          id: kLayerZIndex2,
          type: 'symbol',
          source: 'empty'
        });
        // eslint-disable-next-line no-unused-vars
        const zIndex1 = this.map.addLayer({
          id: kLayerZIndex1,
          type: 'symbol',
          source: 'empty'
        }, kLayerZIndex2); // place this layer below zIndex2
      }
    },
    updateMarkers: debounce(function () {
      const bounds = this.map.getBounds();
      const screenWidth = this.$refs.map.$el.clientWidth;
      const boundsWidths = {
        lng: Math.abs(bounds['_sw'].lng - bounds['_ne'].lng) / screenWidth,
        lat: Math.abs(bounds['_sw'].lat - bounds['_ne'].lat) / screenWidth
      };
      if (Object.keys(this.lastBoundsRefresh || {}).some(position => {
        return Object.keys(get(this.lastBoundsRefresh, position, [])).some(latOrLong => {
          // can move the map around for 5 pixels before refreshing, little performance optimisation
          // mostly only valid for the same zoom-level
          return Math.abs(bounds[position][latOrLong] - get(this.lastBoundsRefresh, `${position}.${latOrLong}`)) > boundsWidths[latOrLong] * 5;
        });
      })) {
        this.requestClusterRequest(bounds);
      }
    }, 333),
    debouncedRequestClusterRequest: debounce(function () {
      this.requestClusterRequest();
    }, 333),
    requestClusterRequest(bounds = undefined) {
      this.lastBoundsRefresh = bounds || this.map ? this.map.getBounds() : null;
      this.$emit('map-moved', {
        bounds: this.lastBoundsRefresh ? [[this.lastBoundsRefresh['_ne'].lng, this.lastBoundsRefresh['_ne'].lat], [this.lastBoundsRefresh['_sw'].lng, this.lastBoundsRefresh['_sw'].lat]] : null
      });
      if (this.clusterRefreshInterval) {
        clearInterval(this.clusterRefreshInterval);
      }
      this.clusterRefreshInterval = setInterval(this.requestClusterRequest, 45000);
    },
    zoomIntoCluster(cluster) {
      this.map.fitBounds(cluster.bbox);
    },
    markerClicked(item) {
      this.selectedItem = item;
      this.zoomToCoordinates(get(item, 'coordinates.coordinates'));
    },
    zoomToCoordinates(coordinates) {
      if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 2) {
        return;
      }
      const bounds = this.map.getBounds();
      const mapDimensions = {
        width: this.$refs.map.$el.clientWidth,
        height: this.$refs.map.$el.clientHeight
      };

      // do not change the zoom-level, if there is no polygon, as there is nothing to zoom to
      let zoomLevel = undefined;
      let boundsWidth = Math.abs(bounds['_sw']['lng'] - bounds['_ne']['lng']);

      // the popup window is 600px wide, so offset half of that
      const moveLng = boundsWidth / mapDimensions.width * 300;
      let flyToParams = {
        center: [coordinates[0] + moveLng, coordinates[1]],
        bearing: 0,
        duration: 2000,
        animate: true
      };
      if (zoomLevel) {
        flyToParams['zoom'] = Math.min(Math.max(this.map.getZoom() - 1.0, zoomLevel), this.map.getZoom() + 1.0);
        flyToParams['pitch'] = 25;
      }
      this.map.flyTo(flyToParams);
    }
  }
};